// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Dimensions } from 'react-native';
import Svg, { Line, Polyline, Rect, Text as SvgText } from 'react-native-svg';

import { Text, View } from '@/components/Themed';

import type { Player, TourTotalsByHoleResponse } from '../types';
import { styles } from '../styles';

const trimTrailingMissingTotals = (totals: number[]) => {
  let lastIndex = totals.length - 1;
  while (lastIndex >= 0) {
    const value = totals[lastIndex];
    if (Number.isFinite(value)) break;
    lastIndex -= 1;
  }
  return lastIndex >= 0 ? totals.slice(0, lastIndex + 1) : [];
};

type Props = {
  series: TourTotalsByHoleResponse['series'];
  players: Player[];
  holes: TourTotalsByHoleResponse['holes'];
  rounds: TourTotalsByHoleResponse['rounds'];
  lastHoleIndexByPlayer?: Record<string, number>;
};

export function ResultsGraph({ series, players, holes, rounds, lastHoleIndexByPlayer }: Props) {
  const width = Math.max(280, Dimensions.get('window').width - 64);
  const height = 190;
  const padding = 28;
  const labelPadding = 12;
  const nameLookup = new Map(players.map((p) => [p.id, p.name]));
  const palette = ['#f6d98a', '#8fd0ff', '#ff9f9f', '#a7f3d0', '#c4b5fd', '#f9a8d4'];
  const normalizedSeries = series
    .map((entry) => ({
      playerId: entry.playerId ?? entry.PlayerId ?? '',
      totals: trimTrailingMissingTotals(entry.totals ?? entry.Totals ?? []),
    }))
    .filter((entry) => entry.playerId);
  const lastHoleIndexMap = lastHoleIndexByPlayer ?? {};
  const seriesWithCutoff = normalizedSeries.map((entry) => {
    const lastIndex = lastHoleIndexMap[entry.playerId];
    if (lastIndex === undefined) return entry;
    const capped = Math.min(lastIndex + 1, entry.totals.length);
    return { ...entry, totals: entry.totals.slice(0, capped) };
  });
  const maxSeriesLength = Math.max(0, ...seriesWithCutoff.map((entry) => entry.totals.length));
  const visibleCount = maxSeriesLength > 0 ? maxSeriesLength : holes.length;
  const visibleHoles = visibleCount > 0 ? holes.slice(0, visibleCount) : holes;

  const allValues = seriesWithCutoff.flatMap((entry) => entry.totals);
  const maxValue = Math.max(1, ...allValues);
  const minValue = Math.min(0, ...allValues);
  const count = Math.max(visibleHoles.length, seriesWithCutoff[0]?.totals.length ?? 0);
  const step = count > 1 ? (width - padding - labelPadding) / (count - 1) : 0;
  const originX = padding;
  const originY = height - padding;
  const maxLabel = Math.ceil(maxValue / 5) * 5;
  const minLabel = Math.floor(minValue / 5) * 5;
  const range = Math.max(1, maxLabel - minLabel);
  const midLabel = minLabel + Math.ceil(range / 2);
  const roundLabelById = new Map(
    rounds.map((round) => {
      const id = round.roundId ?? round.RoundId ?? '';
      const name = round.name ?? round.Name ?? round.courseName ?? round.CourseName ?? '';
      const label = name.length > 20 ? `${name.slice(0, 20)}…` : name;
      return [id, label];
    })
  );
  const holeLabels = visibleHoles
    .map((hole, index) => ({
      index,
      roundId: hole.roundId ?? hole.RoundId ?? '',
      holeNumber: hole.holeNumber ?? hole.HoleNumber ?? 0,
    }))
    .filter((entry) => entry.holeNumber === 1);
  const roundRanges = visibleHoles.reduce<Record<string, { start: number; end: number }>>((acc, hole, index) => {
    const roundId = hole.roundId ?? hole.RoundId ?? '';
    if (!roundId) return acc;
    if (!acc[roundId]) {
      acc[roundId] = { start: index, end: index };
    } else {
      acc[roundId].end = index;
    }
    return acc;
  }, {});
  const orderedRoundIds = visibleHoles.reduce<string[]>((acc, hole) => {
    const roundId = hole.roundId ?? hole.RoundId ?? '';
    if (!roundId || acc.includes(roundId)) return acc;
    acc.push(roundId);
    return acc;
  }, []);

  return (
    <View style={styles.graphWrap}>
      <Svg width={width} height={height}>
        {orderedRoundIds.map((roundId, idx) => {
          const range = roundRanges[roundId];
          if (!range) return null;
          const x = originX + range.start * step;
          const w = (range.end - range.start + 1) * step;
          const fill = idx % 2 === 0 ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.18)';
          return <Rect key={`round-band-${roundId}`} x={x} y={padding} width={w} height={height - padding * 2} fill={fill} />;
        })}
        {orderedRoundIds.map((roundId) => {
          const range = roundRanges[roundId];
          if (!range) return null;
          const hole9Index = range.start + 8;
          if (hole9Index >= range.end) return null;
          const x = originX + hole9Index * step;
          const stroke = 'rgba(255,255,255,0.35)';
          return (
            <Line
              key={`round-hole9-${roundId}`}
              x1={x}
              y1={padding}
              x2={x}
              y2={height - padding}
              stroke={stroke}
              strokeWidth={1}
            />
          );
        })}
        {orderedRoundIds.map((roundId, idx) => {
          if (idx === 0) return null;
          const range = roundRanges[roundId];
          if (!range) return null;
          const x = originX + range.start * step;
          return (
            <Line
              key={`round-boundary-${roundId}`}
              x1={x}
              y1={padding}
              x2={x}
              y2={height - padding}
              stroke="rgba(255,255,255,0.55)"
              strokeWidth={2}
            />
          );
        })}
        <Line
          x1={originX}
          y1={originY}
          x2={width - labelPadding}
          y2={originY}
          stroke="rgba(255,255,255,0.25)"
          strokeWidth={1}
        />
        <Line x1={originX} y1={padding} x2={originX} y2={originY} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
        {[minLabel, midLabel, maxLabel].map((value) => {
          const y = padding + (1 - (value - minLabel) / range) * (height - padding * 2);
          return (
            <SvgText key={`y-${value}`} x={originX - 6} y={y + 4} fontSize="10" fill="#f8fafc" textAnchor="end">
              {value}
            </SvgText>
          );
        })}
        {seriesWithCutoff.map((entry, index) => {
          const color = palette[index % palette.length];
          const points = entry.totals.map((value, idx) => {
            const x = originX + idx * step;
            const y = padding + (1 - (value - minLabel) / range) * (height - padding * 2);
            return `${x},${y}`;
          });
          return <Polyline key={entry.playerId} points={points.join(' ')} fill="none" stroke={color} strokeWidth={2} />;
        })}
        {holeLabels.map((entry) => {
          const label = roundLabelById.get(entry.roundId);
          if (!label) return null;
          const x = originX + entry.index * step;
          if (x < originX || x > width - labelPadding) return null;
          return (
            <SvgText key={`x-${entry.roundId}`} x={x} y={originY + 14} fontSize="9" fill="#f8fafc" textAnchor="start">
              {label}
            </SvgText>
          );
        })}
      </Svg>
      <View style={styles.graphLegend}>
        {seriesWithCutoff.map((entry, index) => {
          const color = palette[index % palette.length];
          return (
            <View key={entry.playerId} style={styles.legendItem}>
              <View style={[styles.legendSwatch, { backgroundColor: color }]} />
              <Text style={styles.legendLabel}>{nameLookup.get(entry.playerId) ?? entry.playerId}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
